import React, { useState } from 'react';
import PropTypes from 'prop-types';
import TextInput from '../ui/TextInput';
import { RULE_INPUT_DECIMALS } from './optimizerRuleRegistry';

const UNIT_SUFFIX = Object.freeze({ percent: '%', bps: 'bps', days: 'days' });
// A disabled limit sits on the page background rather than the disabled fill
// (the field with adornments, or the bare input without).
const DISABLED_CLASS_NAME = 'has-[:disabled]:!bg-bg-base/[0.96] disabled:!bg-bg-base/[0.96]';

function formatInputValue(value, unit, decimals) {
    if (value == null || !Number.isFinite(Number(value))) return '';
    return Number(value).toLocaleString('en-US', {
        maximumFractionDigits: decimals,
        useGrouping: unit === 'usd',
    });
}

// A nonnegative limit input that keeps the user's text while focused, so
// decimals such as "0.35" can be typed; the number is committed on each change.
// Right-aligned, with the ± / $ prefix and the unit suffix inside the field.
export default function RuleLimitInput({
    value,
    onChange,
    unit,
    form = 'max',
    disabled = false,
    ariaLabel,
    width = 118,
    allowNegative = false,
}) {
    const [draft, setDraft] = useState(null);
    const decimals = RULE_INPUT_DECIMALS[unit] ?? 4;
    const pattern = decimals > 0
        ? new RegExp(`^${allowNegative ? '-?' : ''}\\d*(\\.\\d{0,${decimals}})?$`)
        : new RegExp(`^${allowNegative ? '-?' : ''}\\d*$`);
    const prefix = [form === 'abs_max' ? '±' : null, unit === 'usd' ? '$' : null]
        .filter(Boolean)
        .join(' ');
    const suffix = UNIT_SUFFIX[unit] ?? null;

    return (
        <TextInput
            type="text"
            inputMode="decimal"
            aria-label={ariaLabel}
            align="right"
            startAdornment={prefix || null}
            endAdornment={suffix}
            className={DISABLED_CLASS_NAME}
            style={{ width }}
            value={draft ?? formatInputValue(value, unit, decimals)}
            disabled={disabled}
            onFocus={() => setDraft(value == null ? '' : String(value))}
            onBlur={() => setDraft(null)}
            onChange={(event) => {
                const raw = event.target.value.replace(/,/g, '').trim();
                if (!pattern.test(raw)) return;
                setDraft(raw);
                if (raw === '') {
                    onChange(null);
                    return;
                }
                const number = Number(raw);
                if (raw !== '.' && Number.isFinite(number)) onChange(number);
            }}
        />
    );
}

RuleLimitInput.propTypes = {
    value: PropTypes.number,
    onChange: PropTypes.func.isRequired,
    unit: PropTypes.oneOf(['usd', 'percent', 'bps', 'count', 'days', 'ratio']).isRequired,
    form: PropTypes.oneOf(['max', 'abs_max']),
    disabled: PropTypes.bool,
    ariaLabel: PropTypes.string.isRequired,
    width: PropTypes.number,
    allowNegative: PropTypes.bool,
};
